// Module-level registry of marketing program content shipped as static JSON
// per the spec's content-as-data pattern. Each file lives in
// /content/marketing/programs/{slug}.json and matches the shape
// MarketingProgram.jsx renders. Webpack inlines them at build time.
//
// Slug aliasing: the consumer backend uses short slugs ("cognitive",
// "hormone") while the spec/footer use long slugs
// ("cognitive-performance", "energy-vitality"). The resolver below maps
// every known alias to the same JSON file so the editorial content
// always loads regardless of which surface linked here.
import weightManagement from "@/content/marketing/programs/weight-management.json";
import recoveryHealing from "@/content/marketing/programs/recovery-healing.json";
import skinBeauty from "@/content/marketing/programs/skin-beauty.json";
import longevity from "@/content/marketing/programs/longevity.json";
import cognitivePerformance from "@/content/marketing/programs/cognitive-performance.json";
import energyVitality from "@/content/marketing/programs/energy-vitality.json";
import sexualWellness from "@/content/marketing/programs/sexual-wellness.json";

// Each entry: every alias the slug can arrive as → the canonical JSON.
const PROGRAMS = [
  { aliases: ["weight-management", "weight"], content: weightManagement },
  { aliases: ["recovery-healing", "recovery", "healing"], content: recoveryHealing },
  { aliases: ["skin-beauty", "skin", "beauty"], content: skinBeauty },
  { aliases: ["longevity"], content: longevity },
  { aliases: ["cognitive-performance", "cognitive"], content: cognitivePerformance },
  { aliases: ["energy-vitality", "energy", "vitality", "hormone", "hormone-optimization"], content: energyVitality },
  { aliases: ["sexual-wellness", "sexual", "libido", "pt-141", "bremelanotide"], content: sexualWellness },
];

export function getLocalProgramContent(slug) {
  const s = (slug || "").toLowerCase();
  if (!s) return null;
  const hit = PROGRAMS.find((p) => p.aliases.includes(s));
  return hit ? hit.content : null;
}

// Merge local JSON content with API response. Local content (the spec)
// wins for everything editorial — name, tagline, description, status,
// categoryNumber, and every long-form block. Backend wins only for
// runtime/admin-controlled fields (id, isActive, colors, hero image).
export function mergeMarketingProgram(local, backend) {
  if (!local && !backend) return null;
  if (!local) return backend;
  if (!backend) return local;
  const runtime = {};
  if (backend.id != null) runtime.id = backend.id;
  if (backend.isActive != null) runtime.isActive = backend.isActive;
  if (backend.isVisibleOnLanding != null) runtime.isVisibleOnLanding = backend.isVisibleOnLanding;
  if (backend.color) runtime.color = backend.color;
  if (backend.accentColor) runtime.accentColor = backend.accentColor;
  if (backend.darkColor) runtime.darkColor = backend.darkColor;
  if (backend.colors) runtime.colors = backend.colors;
  if (backend.heroImageUrl) runtime.heroImageUrl = backend.heroImageUrl;
  // CMS-ready: when the marketing API ships a per-program `seo` block,
  // it overrides the local one wholesale. Until then, local wins.
  if (backend.seo && typeof backend.seo === "object") runtime.seo = { ...local.seo, ...backend.seo };
  return { ...local, ...runtime };
}
